import React from "react";

/* BrandMark — MASTER_SPEC Draft80 §4.8 (BRAND-01).
 *
 * The crowned bee is the owner's asset and is used as supplied: the SVG
 * master below is a VTracer path conversion of her supplied art (two-tone
 * gold + black paths, no redrawing or recolouring), chosen by the owner as
 * the canonical master. PNG variants generated from her transparent master
 * remain in brand/ as fallback and are not referenced by this component.
 *
 * The mark never appears on printable postcard artwork — that boundary lives
 * in carecard/print_layout.py and is tested there.
 */

const SIZES = { sm: 32, md: 40, lg: 56 };

/* V47 (2026-11-04, owner): her clean flat-gold crowned-bee vector is the
 * mark on the (black) site chrome. The older two-tone master (black outline)
 * stays for the opt-in light theme, where flat gold would sit on white;
 * CSS shows exactly one of the two. The light one is lazy so the default
 * dark theme never downloads it. */
const BRAND_SRC = "/brand/crowned-bee-gold.svg";
const BRAND_SRC_LIGHT = "/brand/crowned-bee.svg";
/* Web crop of her full logo: identical artwork to carecard-express-logo.svg
 * (1920x819, kept for share images); only the outer black margin is trimmed
 * to the postcard border so it sits cleanly on the black page. */
export const LOGO_SRC = "/brand/carecard-express-logo-web.svg";
const LOGO_RATIO = 580 / 1806;
export const CROWN_SRC = "/brand/crown-gold.svg";
/* V48 (Bee, Nov 4 2026: "that's the default crown from now on"): her balanced
 * five-point crown, flat #D4AF37 on transparent, viewBox 450 x 236. */
const CROWN_RATIO = 450 / 236;

export function BrandMark({ size = "md", withWordmark = true, tagline = false, taglineText = "A Message That Matters.", className = "" }) {
  const px = SIZES[size] || SIZES.md;
  return (
    <span className={`cc-brand cc-brand-${size} ${className}`.trim()}>
      <img
        src={BRAND_SRC}
        width={px}
        height={px}
        alt=""
        aria-hidden="true"
        className="cc-brand-mark cc-brand-mark-dark"
        decoding="async"
      />
      <img
        src={BRAND_SRC_LIGHT}
        width={px}
        height={px}
        alt=""
        aria-hidden="true"
        className="cc-brand-mark cc-brand-mark-light"
        decoding="async"
        loading="lazy"
      />
      {withWordmark && (
        <span className="cc-brand-text" aria-hidden="true">
          <span className="cc-brand-word">CareCard Express</span>
          {tagline && <span className="cc-brand-tagline">{taglineText}</span>}
        </span>
      )}
      {/* The image is decorative; the accessible name comes from the wordmark.
          The wordmark can also be hidden by CSS on narrow screens, so the
          screen-reader name is always present rather than conditional. */}
      <span className="cc-sr-only">CareCard Express</span>
    </span>
  );
}

export default BrandMark;

/* FlagshipLockup — BUILD-22 §20.22.
 *
 * The owner-approved flagship CareCard Express lockup (variant E, her
 * 2026-10-11 choice): crown + wordmark, gold on black. The raster is used
 * exactly as supplied — proportional resize only, never redrawn, traced,
 * recolored, stretched, or reconstructed in CSS. Canonical source:
 * /brand/carecard-logo-e7-flagship.png (promoted from the owner's flagship
 * file per BUILD-22). Use for the full-wordmark role (about page, splash,
 * share imagery); the crowned-bee mark remains the icon role.
 */
/* V47: the lockup is now her postcard + crowned-bee-stamp logo, served as
 * the supplied vector (web crop) instead of the V46 raster copy. */
const FLAGSHIP_SRC = LOGO_SRC;

export function FlagshipLockup({ width = 320, className = "" }) {
  return (
    <span className={`cc-flagship ${className}`.trim()}>
      <img
        src={FLAGSHIP_SRC}
        width={width}
        height={Math.round(width * LOGO_RATIO)}
        alt=""
        aria-hidden="true"
        className="cc-flagship-lockup"
        decoding="async"
      />
      <span className="cc-sr-only">CareCard Express</span>
    </span>
  );
}

/* CrownAccent — V47. Her crown alone, flat gold, decorative only. Used
 * sparingly: home "How it works" heading, 404 page, and (V48) the info-page
 * sub-nav brand. In the light theme CSS renders it in dark ink (no gold on
 * light backgrounds). */
export function CrownAccent({ size = 22, className = "" }) {
  return (
    <img
      src={CROWN_SRC}
      width={Math.round(size * CROWN_RATIO)}
      height={size}
      alt=""
      aria-hidden="true"
      className={`cc-crown-accent ${className}`.trim()}
      decoding="async"
    />
  );
}

/* BrandLogo — V47. Her full CareCard Express logo (gold lettering, postal
 * lines, crowned-bee stamp, double postcard border on black), used as
 * supplied. Decorative image; the accessible name comes from `label`. */
export function BrandLogo({ width = 300, className = "", label = "CareCard Express", eager = false }) {
  return (
    <span className={`cc-logo ${className}`.trim()}>
      <img
        src={LOGO_SRC}
        width={width}
        height={Math.round(width * LOGO_RATIO)}
        alt=""
        aria-hidden="true"
        className="cc-logo-img"
        decoding="async"
        {...(eager ? { fetchpriority: "high" } : { loading: "lazy" })}
      />
      {label ? <span className="cc-sr-only">{label}</span> : null}
    </span>
  );
}
